/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, Input, NgZone, OnChanges, OnDestroy} from '@angular/core';
import {handleCopyModalResult} from '../common/util/handle-copy-modal-result';
import {ToastrService} from 'ngx-toastr';
import * as _ from 'lodash';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AngularModalService} from '../common/modal/angular-modal.service';
import {AdminResourceCopyConfig} from '../common/controller/admin-resource-copy-config';
import {wireModelCommsModelActions} from '../common/controller/model-comms-actions';
import {CreativeSetCopyModalComponent} from './creative-set-copy-modal.component';
import {SpecifyIdModalComponent} from '../common/directives/specify-id-modal.component';
import template from './creative-set-context-actions.component.html';
import {CreativeSetResource} from '../../components/api/resources/resource/creative-set.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {StateService} from '@uirouter/core';

const OBJECT_STATE_METHODS: Record<string, string> = {
  ACTIVE: '$activate',
  INACTIVE: '$archive',
  HIDDEN: '$delete'
};

@Component({
  standalone: false,
  selector: 'adn-creative-set-context-actions',
  template
})
export class CreativeSetContextActionsComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() blockDelete: any;

  hideHidden = false;
  rotateACTIVE = false;
  rotateINACTIVE = false;
  rotateHIDDEN = false;

  readonly copyConfig: AdminResourceCopyConfig = {
    modalComponent: CreativeSetCopyModalComponent,
    stateUrl: 'app.adv.creative-sets.creative-set',
    lcase: 'creative set',
    ucase: 'Creative Set'
  };

  private subscribedModelComms: any;
  private destroyed = false;

  constructor(
    @Inject(CreativeSetResource) private readonly creativeSet: any,
    private readonly $state: StateService,
    private readonly eventBus: AppEventBusService,
    private readonly toastr: ToastrService,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone
  ) {
    this.hideHidden = LocalNetworkProfile.isPureMarketplacePlatform();
  }

  get resolvedIsNew(): boolean {
    return !!this.isNew;
  }

  ngOnChanges(): void {
    if (this.modelComms) {
      wireModelCommsModelActions(this.modelComms, this.creativeSet);
    }
    this.ensureModelCommsSubscription();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  setObjectState(objectState: string): void {
    if (this.readOnly || this.resolvedIsNew || this.blockDelete || this.model?.objectState === objectState) {
      return;
    }
    this.invokeObjectStateChange(objectState);
  }

  specifyId(): void {
    const model = this.model;
    const modelComms = this.modelComms;
    if (!model || !modelComms) {
      return;
    }
    this.angularModalService.show(SpecifyIdModalComponent, {
      initialState: {model, modelComms}
    });
  }

  copy(): void {
    if (this.readOnly || this.blockDelete || !this.model) {
      return;
    }

    const modalRef = this.angularModalService.show(this.copyConfig.modalComponent, {
      initialState: {model: this.model}
    });
    (modalRef.content as any).onCopied.subscribe((result: any) => {
      handleCopyModalResult(() => {
        if (result.destination) {
          this.$state.go(this.copyConfig.stateUrl, {id: result.model.id}, {inherit: false});
        } else {
          this.toastr.success(
            'A copy of this ' + this.copyConfig.lcase + ' was successfully created.',
            this.copyConfig.ucase + ' Copied'
          );
        }
      });
    });
  }

  private invokeObjectStateChange(objectState: string): void {
    if (!this.model?.id || !this.modelComms) {
      return;
    }

    const methodName = OBJECT_STATE_METHODS[objectState];
    const resource = new this.creativeSet({id: this.model.id});
    const action = resource && resource[methodName];
    if (!_.isFunction(action)) {
      this.toastr.error('Resource is missing ' + methodName, 'State change failed');
      return;
    }

    this.setRotating(objectState, true);
    const onDone = () => {
      this.ngZone.run(() => {
        this.setRotating(objectState, false);
        this.eventBus.runAsync();
      });
    };

    const onSuccess = (result: any) => {
      this.reloadModelFromApi(result).finally(() => onDone());
    };

    const onError = () => {
      this.ngZone.run(() => {
        this.toastr.error('Failed to update creative set state', 'State change failed');
        onDone();
      });
    };

    action.call(resource, onSuccess, onError);
  }

  private reloadModelFromApi(stateChangeResult?: any): Promise<void> {
    if (!this.model?.id) {
      return Promise.resolve();
    }

    if (stateChangeResult?.objectState !== undefined) {
      this.model.objectState = stateChangeResult.objectState;
    }

    return this.creativeSet.get({id: this.model.id}).$promise.then((fresh: any) => {
      this.ngZone.run(() => {
        if (fresh?.objectState !== undefined) {
          this.model.objectState = fresh.objectState;
        }
        this.model.validationWarnings = _.cloneDeep(fresh?.validationWarnings || []);
        if (fresh?.status !== undefined) {
          this.model.status = fresh.status;
        }
        this.modelComms.modelUpdated(this.model);
      });
    });
  }

  private setRotating(objectState: string, rotating: boolean): void {
    if (objectState === 'ACTIVE') {
      this.rotateACTIVE = rotating;
    } else if (objectState === 'INACTIVE') {
      this.rotateINACTIVE = rotating;
    } else if (objectState === 'HIDDEN') {
      this.rotateHIDDEN = rotating;
    }
  }

  private ensureModelCommsSubscription(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }

    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNew: boolean) => {
      if (this.destroyed || type || !this.model || !data || this.model === data) {
        if (!this.destroyed && typeof isNew === 'boolean') {
          this.isNew = isNew;
        }
        return;
      }

      if (data.name !== undefined) {
        this.model.name = data.name;
      }
      if (data.objectState !== undefined) {
        this.model.objectState = data.objectState;
      }
      if (data.validationWarnings !== undefined) {
        this.model.validationWarnings = _.cloneDeep(data.validationWarnings);
      }
      if (data.status !== undefined) {
        this.model.status = data.status;
      }

      if (typeof isNew === 'boolean') {
        this.isNew = isNew;
      }
    });
  }
}
